<script setup lang="ts">
import { computed } from 'vue'
import Icon from './Icon.vue'
import ScrollAnimate from './ScrollAnimate.vue'
import { useI18n } from '../composables/useI18n'

const { t } = useI18n()

const diagram = computed(() => t('how.diagram') as Record<string, string>)
const steps = computed(() => t('how.steps') as string[])
</script>

<template>
  <section id="how" class="py-20 bg-base-200">
    <div class="max-w-6xl mx-auto px-4">
      <ScrollAnimate as="div" class="text-center max-w-3xl mx-auto mb-12">
        <h2 class="text-3xl md:text-4xl font-bold text-base-content">{{ t('how.title') }}</h2>
        <p class="text-lg text-base-content/70 mt-4">
          {{ t('how.subtitle') }}
        </p>
      </ScrollAnimate>

      <ScrollAnimate as="div" class="card bg-base-100 border border-base-300 shadow-md overflow-hidden">
        <div class="card-body p-4 md:p-8">
          <div class="w-full overflow-x-auto">
            <svg
              viewBox="0 0 800 260"
              class="w-full min-w-[600px] h-auto"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              :aria-label="t('how.ariaLabel')"
            >
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3.5, 0 7" fill="#1e90ff" />
                </marker>
              </defs>

              <!-- Client Agent -->
              <g class="pulse-node">
                <rect x="20" y="90" width="150" height="100" rx="12" fill="#e8f4ff" stroke="#1e90ff" stroke-width="2" />
                <text x="95" y="128" text-anchor="middle" class="fill-base-content font-semibold text-sm">{{ diagram.client }}</text>
                <text x="95" y="150" text-anchor="middle" class="fill-base-content/70 text-xs">{{ diagram.clientDesc1 }}</text>
                <text x="95" y="170" text-anchor="middle" class="fill-base-content/70 text-xs">{{ diagram.clientDesc2 }}</text>
              </g>

              <!-- OpenAaaS Server -->
              <g class="pulse-node" style="animation-delay: 0.3s">
                <rect x="240" y="90" width="150" height="100" rx="12" fill="#e8f4ff" stroke="#1e90ff" stroke-width="2" />
                <text x="315" y="128" text-anchor="middle" class="fill-base-content font-semibold text-sm">{{ diagram.server }}</text>
                <text x="315" y="150" text-anchor="middle" class="fill-base-content/70 text-xs">{{ diagram.serverDesc1 }}</text>
                <text x="315" y="170" text-anchor="middle" class="fill-base-content/70 text-xs">{{ diagram.serverDesc2 }}</text>
              </g>

              <!-- Agent Core -->
              <g class="pulse-node" style="animation-delay: 0.6s">
                <rect x="460" y="90" width="150" height="100" rx="12" fill="#e8f4ff" stroke="#1e90ff" stroke-width="2" />
                <text x="535" y="128" text-anchor="middle" class="fill-base-content font-semibold text-sm">{{ diagram.core }}</text>
                <text x="535" y="150" text-anchor="middle" class="fill-base-content/70 text-xs">{{ diagram.coreDesc1 }}</text>
                <text x="535" y="170" text-anchor="middle" class="fill-base-content/70 text-xs">{{ diagram.coreDesc2 }}</text>
              </g>

              <!-- Local Data -->
              <g class="pulse-node" style="animation-delay: 0.9s">
                <rect x="680" y="90" width="110" height="100" rx="12" fill="#f5f8fb" stroke="#536878" stroke-width="2" />
                <text x="735" y="135" text-anchor="middle" class="fill-base-content font-semibold text-sm">{{ diagram.data }}</text>
                <text x="735" y="160" text-anchor="middle" class="fill-base-content/70 text-xs">{{ diagram.dataDesc }}</text>
              </g>

              <!-- Forward arrows -->
              <path
                id="flow1"
                d="M 170 140 L 240 140"
                stroke="#1e90ff"
                stroke-width="2"
                stroke-dasharray="6 6"
                marker-end="url(#arrowhead)"
                class="flow-line"
              />
              <path
                id="flow2"
                d="M 390 140 L 460 140"
                stroke="#1e90ff"
                stroke-width="2"
                stroke-dasharray="6 6"
                marker-end="url(#arrowhead)"
                class="flow-line"
                style="animation-delay: 0.4s"
              />
              <path
                id="flow3"
                d="M 610 140 L 680 140"
                stroke="#1e90ff"
                stroke-width="2"
                stroke-dasharray="6 6"
                marker-end="url(#arrowhead)"
                class="flow-line"
                style="animation-delay: 0.8s"
              />

              <!-- Moving particles -->
              <g class="motion-particles">
                <circle r="4" fill="#1e90ff">
                  <animateMotion dur="1.6s" repeatCount="indefinite">
                    <mpath href="#flow1" />
                  </animateMotion>
                </circle>
                <circle r="4" fill="#1e90ff">
                  <animateMotion dur="1.6s" begin="0.5s" repeatCount="indefinite">
                    <mpath href="#flow2" />
                  </animateMotion>
                </circle>
                <circle r="4" fill="#1e90ff">
                  <animateMotion dur="1.6s" begin="1s" repeatCount="indefinite">
                    <mpath href="#flow3" />
                  </animateMotion>
                </circle>
              </g>

              <!-- Labels -->
              <text x="205" y="158" text-anchor="middle" class="fill-base-content text-xs font-medium">{{ diagram.labelRequest }}</text>
              <text x="425" y="158" text-anchor="middle" class="fill-base-content text-xs font-medium">{{ diagram.labelRoute }}</text>
              <text x="645" y="158" text-anchor="middle" class="fill-base-content text-xs font-medium">{{ diagram.labelLocal }}</text>
            </svg>
          </div>

          <div class="grid sm:grid-cols-3 gap-4 mt-6 text-sm text-base-content/70">
            <div v-for="(step, index) in steps" :key="index" class="flex items-start gap-2">
              <span class="badge badge-primary badge-xs mt-0.5">{{ index + 1 }}</span>
              <span>{{ step }}</span>
            </div>
          </div>
        </div>
      </ScrollAnimate>
    </div>
  </section>
</template>
